import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function ConsentPage() {
  const navigate = useNavigate();
  const [isUnder18, setIsUnder18] = useState<boolean | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [guardianInfo, setGuardianInfo] = useState({
    guardianName: '',
    guardianPhone: '',
    guardianEmail: '',
    guardianRelation: ''
  });

  const handleAgeSelection = (under18: boolean) => {
    setIsUnder18(under18);
    if (!under18) {
      setGuardianInfo({ guardianName: '', guardianPhone: '', guardianEmail: '', guardianRelation: '' });
    }
  };

  const handleGuardianChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setGuardianInfo(prev => ({ ...prev, [name]: value }));
  };

  const canProceed = () => {
    if (!agreedToTerms) return false;
    if (isUnder18 === null) return false;
    if (isUnder18 && (!guardianInfo.guardianName.trim() || !guardianInfo.guardianPhone.trim() || !guardianInfo.guardianEmail.trim() || !guardianInfo.guardianRelation.trim())) {
      return false;
    }
    return true;
  };

  const handleProceed = () => {
    if (canProceed()) {
      // 保護者情報をlocalStorageに保存（予約フォームで使用）
      if (isUnder18) {
        localStorage.setItem('guardianInfo', JSON.stringify(guardianInfo));
      } else {
        localStorage.removeItem('guardianInfo');
      }
      
      navigate('/', { replace: false });
      setTimeout(() => {
        document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleBookingClick = () => {
    navigate('/', { replace: false });
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Klee One', cursive" }}>
            こころケア
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">ホーム</Link>
            <Link to="/profile" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">自己紹介</Link>
            <Link to="/terms" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">注意事項</Link>
            <Link to="/faq" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">よくある質問</Link>
          </div>

          <button 
            onClick={handleBookingClick}
            className="bg-[#FF9B8A] hover:bg-[#FF8A77] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            今すぐ予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5F3] via-white to-[#F0F9FF]"></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-[#FF9B8A]/10 rounded-full mb-6">
            <span className="text-[#FF9B8A] text-sm font-medium">予約前の確認</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
            同意確認
          </h1>
          
          <p className="text-lg text-gray-600">
            予約を進める前に、以下の内容をご確認ください
          </p>
        </div>
      </section>

      {/* Consent Form */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          {/* Step 1: Age Confirmation */}
          <div className="bg-gradient-to-br from-[#F0F9FF] to-white rounded-3xl p-8 md:p-10 border-2 border-[#A8D5E2]/30 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#A8D5E2] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">年齢確認</h2>
                <p className="text-gray-600 text-sm">あなたの年齢を教えてください</p>
              </div>
            </div>

            <div className="ml-16 space-y-4">
              <button
                onClick={() => handleAgeSelection(false)}
                className={`w-full p-5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  isUnder18 === false
                    ? 'border-[#A8D5E2] bg-[#A8D5E2]/10 shadow-md'
                    : 'border-gray-200 hover:border-[#A8D5E2]/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isUnder18 === false ? 'border-[#A8D5E2] bg-[#A8D5E2]' : 'border-gray-300'
                  }`}>
                    {isUnder18 === false && (
                      <i className="ri-check-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">18歳以上です</div>
                    <div className="text-sm text-gray-600">保護者の同意は不要です</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleAgeSelection(true)}
                className={`w-full p-5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  isUnder18 === true
                    ? 'border-[#FFD93D] bg-[#FFD93D]/10 shadow-md'
                    : 'border-gray-200 hover:border-[#FFD93D]/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isUnder18 === true ? 'border-[#FFD93D] bg-[#FFD93D]' : 'border-gray-300'
                  }`}>
                    {isUnder18 === true && (
                      <i className="ri-check-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">18歳未満です</div>
                    <div className="text-sm text-gray-600">保護者の同意が必要です</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Step 2: Guardian Information (if under 18) */}
          {isUnder18 === true && (
            <div className="bg-gradient-to-br from-[#FFFBF0] to-white rounded-3xl p-8 md:p-10 border-2 border-[#FFD93D]/30 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#FFD93D] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">保護者情報</h2>
                  <p className="text-gray-600 text-sm">保護者の方の情報を入力してください</p>
                </div>
              </div>

              <div className="ml-16 space-y-5">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-yellow-700 text-lg w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <p className="text-sm text-yellow-900">
                    18歳未満の方がご利用される場合、保護者の同意が必要です。緊急時や必要と判断した場合、保護者の方へご連絡させていただくことがあります。ご入力いただいたメールアドレスに確認メールをお送りします。
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    保護者のお名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="guardianName"
                    value={guardianInfo.guardianName}
                    onChange={handleGuardianChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm"
                    placeholder="山田 花子"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    続柄 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="guardianRelation"
                    value={guardianInfo.guardianRelation}
                    onChange={handleGuardianChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="父">父</option>
                    <option value="母">母</option>
                    <option value="祖父">祖父</option>
                    <option value="祖母">祖母</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    保護者のメールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="guardianEmail"
                    value={guardianInfo.guardianEmail}
                    onChange={handleGuardianChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm"
                    placeholder="parent@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ※ サービス内容の確認メールをお送りします
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    保護者の電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="guardianPhone"
                    value={guardianInfo.guardianPhone}
                    onChange={handleGuardianChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm"
                    placeholder="090-1234-5678"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ※ 緊急時の連絡先として使用します
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Terms Agreement */}
          <div className="bg-gradient-to-br from-[#FFF5F3] to-white rounded-3xl p-8 md:p-10 border-2 border-[#FF9B8A]/30 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF9B8A] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">{isUnder18 === true ? '3' : '2'}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">注意事項への同意</h2>
                <p className="text-gray-600 text-sm">以下の内容をご確認の上、同意してください</p>
              </div>
            </div>

            <div className="ml-16 space-y-5">
              {/* Terms Summary */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 max-h-96 overflow-y-auto">
                <h3 className="font-bold text-gray-900 mb-4">重要な注意事項（要約）</h3>
                <div className="space-y-4 text-sm text-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>医療行為ではありません：</strong>本サービスは心理学を学ぶ大学生が提供するカウンセリングサービスであり、医療行為ではありません。</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>対象者：</strong>日常的な悩みや心のケアを目的とした方が対象です。精神疾患の診断を受けている方、自傷行為や自殺念慮がある方は医療機関への受診をお勧めします。</p>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-blue-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                      対応できる相談
                    </h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li>• 恋愛の悩み、体の変化</li>
                      <li>• 食べ方の悩み、体重に囚われる悩み</li>
                      <li>• 一つの考えに縛られる、安心するための行動が止まらない悩み</li>
                      <li>• 親との関係、毎日の小さな不安</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                    <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                      対応できない相談
                    </h4>
                    <ul className="space-y-2 text-sm text-red-800">
                      <li>• 精神疾患の診断を受けている方</li>
                      <li>• 自傷・自殺の具体的計画がある相談</li>
                      <li>• 現在進行形の虐待・犯罪行為</li>
                      <li>• 医学的治療が必要な症状の判断</li>
                    </ul>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>緊急時の対応：</strong>生命・安全に危険が高いと判断した場合、セッションを中断し、医療機関や保護者へ連絡する場合があります。</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>責任の範囲：</strong>本サービスの利用は、ご自身の判断と責任において行っていただきます。セッション内容の実践により生じた結果について、当サービスは責任を負いかねます。</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>守秘義務：</strong>セッション内容は厳重に守秘しますが、生命・身体に危険が及ぶ可能性がある場合や、虐待・犯罪行為が疑われる場合は例外となります。</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>キャンセル：</strong>キャンセルや日程変更は、セッション開始の24時間前までにご連絡ください。</p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#FF9B8A] text-base w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <p><strong>個人情報：</strong>ご提供いただいた個人情報は、セッションの実施と連絡のみに使用します。</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Link 
                    to="/terms" 
                    className="text-[#FF9B8A] hover:text-[#FF8A77] text-sm font-medium flex items-center gap-2 cursor-pointer"
                  >
                    <span>詳細な注意事項を確認する</span>
                    <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>

              {/* Agreement Checkbox */}
              <label className="flex items-start gap-4 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                    agreedToTerms 
                      ? 'bg-[#FF9B8A] border-[#FF9B8A]' 
                      : 'border-gray-300 group-hover:border-[#FF9B8A]'
                  }`}>
                    {agreedToTerms && (
                      <i className="ri-check-line text-white text-base w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">
                    上記の注意事項・免責事項を確認し、同意します
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>本サービスが医療・治療を目的としたものではないこと</strong>、心理療法・精神療法・治療行為には該当しないこと、および利用は自己責任であることを理解しました
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Proceed Button */}
          <div className="text-center">
            <button
              onClick={handleProceed}
              disabled={!canProceed()}
              className={`px-12 py-4 rounded-full text-base font-bold transition-all whitespace-nowrap shadow-lg ${
                canProceed()
                  ? 'bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white cursor-pointer hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              予約フォームへ進む →
            </button>

            {!canProceed() && (
              <p className="text-sm text-gray-500 mt-4">
                すべての項目を確認・入力してください
              </p>
            )}
          </div>

          {/* Emergency Contact */}
          <div className="mt-12 bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-alert-line text-red-600 text-xl w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-2">緊急時の相談窓口</h3>
                <p className="text-sm text-red-800 mb-3">
                  心身の不調が続く場合や、深刻な症状がある場合は、必ず医療機関を受診してください。
                </p>
                <div className="text-sm text-red-900 space-y-1">
                  <p><strong>いのちの電話：</strong>0120-783-556（24時間対応）</p>
                  <p><strong>東京いのちの電話：</strong>03-3264-4343（24時間対応）</p>
                  <p><strong>こころの健康相談統一ダイヤル：</strong>0570-064-556</p>
                  <p><strong>児童相談所虐待対応ダイヤル：</strong>189（24時間対応）</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
